// ./src/hooks/useAccountType.ts
//
// What the signed-in account runs — `account.account_type` (ms_auth; the choices are `config/accountTypes`). Read by
// Home's Getting started card (a venue's "save your venue" step, ROADMAP #161 d) and the account onboarding (#161), so
// both decide the venue step on the same read.
//
// ⚠ A read that has not answered, or FAILED, stays UNKNOWN (`undefined`) — never "not a venue" (that would hide the
//   venue's step) and never "a venue". `null` is a real answer: the account chose no type.
// ⚠ Only the latest read is kept: a slow earlier one must not overwrite a newer one (or another account's).
import { useCallback, useEffect, useRef, useState } from 'react';
import { OpResult, OP_RESULT_CODES } from '@sdflc/api-helpers';

import { AccountService } from '@/utils/api';

export interface AccountTypeRead {
  /** The type; `null` = none chosen; `undefined` = not read yet, or the read failed. */
  accountType: string | null | undefined;
  /** #198 b — the account's name, from the same read (the organizer page starts with it); undefined until read. */
  accountName: string | null | undefined;
  /** The last read FAILED (so `undefined` will not resolve on its own). */
  failed: boolean;
  /** Why it failed — for an `ErrorState`; null otherwise. */
  error: OpResult | null;
  /** Read again. */
  reload: () => Promise<void>;
}

/**
 * `enabled` false (no account, a crew session, nobody needs it) reads nothing. ⚠ Required, no default: a gate that
 * defaults open is a gate a caller skips without noticing.
 */
export const useAccountType = (accountId: string | null | undefined, enabled: boolean): AccountTypeRead => {
  const [accountType, setAccountType] = useState<string | null | undefined>(undefined);
  const [accountName, setAccountName] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<OpResult | null>(null);
  const callRef = useRef(0);

  const reload = useCallback(async (): Promise<void> => {
    if (!enabled || !accountId) {
      return;
    }

    const call = ++callRef.current;

    setError(null);

    try {
      const res = await AccountService.get(accountId);

      if (call !== callRef.current) {
        return;
      }

      if (res.code === OP_RESULT_CODES.OK) {
        const account = res.getDataFirst() as { accountType?: string | null; name?: string | null } | undefined;

        setAccountType(account?.accountType ?? null);
        setAccountName(account?.name ?? null);
      } else {
        setAccountType(undefined);
        setError(res);
      }
    } catch (err) {
      if (call === callRef.current) {
        setAccountType(undefined);
        setError(OpResult.fromException(err));
      }
    }
  }, [accountId, enabled]);

  useEffect(() => {
    // A different account is a different answer — forget the last one before reading.
    callRef.current += 1;
    setAccountType(undefined);
    setError(null);

    void reload();
  }, [reload]);

  return { accountType, accountName, failed: !!error, error, reload };
};
